import React, { Fragment } from "react";
import { Tooltip } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import ReviewInvite from "../../icons/js/reviewInviteIcon";
import MessageEnvalopeOpen from "../../icons/js/MessageEnvalopeOpen";
import SmileyColorIcon from "../../icons/js/SmileyColorIcon";
import SadColorIcon from "../../icons/js/SadColorIcon";
import PencilIcon from "../../icons/js/PencilIcon";
import EnvalopeIcon from "../../icons/js/EnvalopeIcon";
import GoogleIconJsx from "../../icons/js/GoogleIcon";
import FacebookIcon from "../../icons/js/facebookIcon";
import { SVGIcon } from "../../library/basicComponents";
import DealerraterIcon from "../../../static/icons/DealerraterIcon.svg";
import YelpIcon from "../../../static/icons/YelpIcon.svg";
import MessageIcon from "../../../static/icons/MessageIcon.svg";
import CarsDotComIcon from "../../../static/icons/carsdotcom.svg";
import EngagedAILogo from "../../../static/EngagedAi_trans_bg.svg";

import {
  getTrackingStagesFromActions,
  getTrackingStagesFromDetails,
  PLATFORM_LABELS,
  PLATFORMS,
  STAGE,
} from "../../helpers/reviewTrackingHelper";

// Tracking timestamps come from the API as UTC; parse as UTC then convert to the
// dealership's location timezone when provided (see currentLocationTimezone),
// falling back to the viewer's local zone so the timeline reads correctly everywhere.
dayjs.extend(utc);
dayjs.extend(timezone);

const STAGE_LABELS = {
  [STAGE.INVITE_SENT]: "Invite sent",
  [STAGE.INVITE_OPENED]: "Invite opened",
  [STAGE.GOOD]: "Positive experience",
  [STAGE.BAD]: "Negative experience",
  [STAGE.FEEDBACK_SUBMITTED]: "Feedback submitted",
  [STAGE.OTTO_USED]: "Review drafted by Otto",
  [STAGE.SELF_WRITTEN]: "Self written review",
  [STAGE.REVIEW_POSTED]: "Review posted",
};

const STAGE_TOOLTIPS = {
  [STAGE.INVITE_SENT]: "Review invite sent",
  [STAGE.INVITE_OPENED]: "Lead opened the invite",
  [STAGE.GOOD]: "Lead indicated a satisfactory experience",
  [STAGE.BAD]: "Lead indicated an unsatisfactory experience",
  [STAGE.FEEDBACK_SUBMITTED]: "Lead submitted feedback",
  [STAGE.OTTO_USED]: "AI-generated review presented to the customer",
  [STAGE.SELF_WRITTEN]: "Review drafted by the customer",
  [STAGE.REVIEW_POSTED]: "Review posted",
};

const PLATFORM_ICON = {
  [PLATFORMS.GOOGLE]: (size, sizePx) => <GoogleIconJsx width={size} height={size} />,
  [PLATFORMS.FACEBOOK]: (size, sizePx) => <FacebookIcon width={size} height={size} />,
  [PLATFORMS.DEALER_RATER]: (size, sizePx) => <SVGIcon size={sizePx} component={DealerraterIcon} />,
  [PLATFORMS.YELP]: (size, sizePx) => <SVGIcon size={sizePx} component={YelpIcon} />,
  [PLATFORMS.CARS_COM]: (size, sizePx) => <SVGIcon size={sizePx} component={CarsDotComIcon} />,
};

const rawLabel = stage => {
  if (!stage.actionType) return "Activity";
  return stage.actionValue ? `${stage.actionType} ${stage.actionValue}` : stage.actionType;
};

export const labelFor = stage => {
  if (stage.key === STAGE.PLATFORM_CLICKED) {
    return `Opened ${PLATFORM_LABELS[stage.platform] || "review site"}`;
  }
  if (stage.key === STAGE.REVIEW_POSTED && stage.platform) {
    return `Posted on ${PLATFORM_LABELS[stage.platform] || "review site"}`;
  }
  if (stage.key === STAGE.UNKNOWN) return rawLabel(stage);
  return STAGE_LABELS[stage.key] || rawLabel(stage);
};

const tooltipFor = stage => {
  if (stage.key === STAGE.PLATFORM_CLICKED) {
    return `Customer opened ${PLATFORM_LABELS[stage.platform] || "review site"}`;
  }
  if (stage.key === STAGE.REVIEW_POSTED && stage.platform) {
    return `Customer posted a review on ${PLATFORM_LABELS[stage.platform] || "review site"}`;
  }
  if (stage.key === STAGE.UNKNOWN) return rawLabel(stage);
  return STAGE_TOOLTIPS[stage.key] || rawLabel(stage);
};

// Tooltip body for a stage. For "Feedback submitted" we surface the lead's
// free-text negative feedback (multiline) when one exists — sourced from the
// Review record (Review.body, reviewType BAD) and passed in as `negativeComment`.
// Per spec, no tooltip is shown for that icon when the lead left no comment.
// All other stages keep their standard descriptive tooltip.
const tooltipContentFor = (stage, negativeComment) => {
  if (stage.key === STAGE.FEEDBACK_SUBMITTED) {
    const comment = (negativeComment || "").trim();
    return comment ? (
      <div className="whitespace-pre-line break-words max-w-[280px]">{comment}</div>
    ) : null;
  }
  return tooltipFor(stage);
};

// Wraps children in a Tooltip only when there's content to show, so the
// feedback-submitted icon renders with no tooltip when there's no comment.
const StageTooltip = ({ stage, negativeComment, children }) => {
  const content = tooltipContentFor(stage, negativeComment);
  if (!content) return children;
  return <Tooltip title={content}>{children}</Tooltip>;
};

export const StageIcon = ({ stage, size }) => {
  const sizePx = `${size}px`;
  const platformIcon = PLATFORM_ICON[stage.platform];
  switch (stage.key) {
    case STAGE.INVITE_SENT:
      return <ReviewInvite width={size} height={size} />;
    case STAGE.INVITE_OPENED:
      return <MessageEnvalopeOpen width={size} height={size} />;
    case STAGE.GOOD:
      return <SmileyColorIcon width={size} height={size} />;
    case STAGE.BAD:
      return <SadColorIcon width={size} height={size} />;
    case STAGE.FEEDBACK_SUBMITTED:
      return <SVGIcon size={sizePx} component={MessageIcon} />;
    case STAGE.OTTO_USED:
      return <SVGIcon size={sizePx} component={EngagedAILogo} />;
    case STAGE.SELF_WRITTEN:
      return <PencilIcon width={size} height={size} />;
    case STAGE.REVIEW_POSTED:
      return platformIcon ? (
        platformIcon(size, sizePx)
      ) : (
        <EnvalopeIcon width={size} height={size} />
      );
    case STAGE.PLATFORM_CLICKED:
      return platformIcon ? platformIcon(size, sizePx) : <UnknownDot size={size} />;
    default:
      return <UnknownDot size={size} />;
  }
};

const UnknownDot = ({ size }) => (
  <span
    style={{
      display: "inline-block",
      width: size,
      height: size,
      borderRadius: "50%",
      background: "#9CA3AF",
    }}
  />
);

export const stageKey = stage =>
  stage.uid || (stage.platform ? `${stage.key}_${stage.platform}` : stage.key);

export const toStages = data => {
  if (!data) return [];
  if (Array.isArray(data)) {
    if (data.length === 0) return [];
    return typeof data[0] === "string"
      ? getTrackingStagesFromActions(data)
      : getTrackingStagesFromDetails(data);
  }
  if (typeof data === "object") {
    const fromTrackings = getTrackingStagesFromDetails(data.trackings);
    if (fromTrackings.length > 0) return fromTrackings;
    return getTrackingStagesFromActions(data.actions);
  }
  return [];
};

export const ReviewTrackingInline = ({ data, negativeComment }) => {
  const stages = toStages(data);
  if (stages.length === 0) return null;

  return (
    <div className="w-[95%] h-full flex flex-col justify-center items-center mx-auto mt-2 max-w-[800px]">
      <div className="flex flex-row flex-wrap w-full items-center justify-center gap-3 mb-2">
        {stages.map((stage, i) => (
          <Fragment key={stageKey(stage)}>
            {i > 0 && <ArrowRightOutlined />}
            <StageTooltip stage={stage} negativeComment={negativeComment}>
              <span className="flex flex-row gap-1 items-center text-xs text-grey-700">
                <StageIcon stage={stage} size={14} />
                {labelFor(stage)}
              </span>
            </StageTooltip>
          </Fragment>
        ))}
      </div>
    </div>
  );
};

export const ReviewTrackingIcons = ({ data, negativeComment }) => {
  const stages = toStages(data);
  if (stages.length === 0) return null;

  return (
    <div className="flex flex-row flex-wrap gap-1 items-center">
      {stages.map(stage => (
        <StageTooltip key={stageKey(stage)} stage={stage} negativeComment={negativeComment}>
          <span className="inline-flex items-center">
            <StageIcon stage={stage} size={14} />
          </span>
        </StageTooltip>
      ))}
    </div>
  );
};

export const ReviewTrackingTimeline = ({ record, currentLocationTimezone }) => {
  const stages = toStages(record?.trackings);

  const enriched = stages.map(stage =>
    stage.key === STAGE.INVITE_SENT && !stage.timestamp
      ? { ...stage, timestamp: record?.invitedDate || record?.createTs || null }
      : stage
  );

  if (enriched.length === 0) {
    return <div className="py-6 px-4 bg-[#FAFAFA] text-[#6B7280] text-xs">No activity yet.</div>;
  }

  const userName = record?.user
    ? `${record.user.firstName || ""} ${record.user.lastName || ""}`.trim()
    : "";

  // Lead's negative feedback (Review.body, reviewType BAD) — shown on the
  // "feedback submitted" step's tooltip when present.
  const negativeComment = record?.review?.body;

  return (
    <div className="py-6 px-5 bg-white overflow-x-auto">
      <div className="flex items-start gap-3 min-w-max">
        {enriched.map((stage, index) => {
          const label = labelFor(stage);
          const feedbackComment =
            stage.key === STAGE.FEEDBACK_SUBMITTED ? (negativeComment || "").trim() : "";
          // Prefer the negative comment for the feedback step; fall back to the
          // stage label so every step still shows a tooltip.
          const tooltipTitle = feedbackComment ? (
            <div className="whitespace-pre-line break-words max-w-[280px]">{feedbackComment}</div>
          ) : (
            label
          );
          return (
            <div key={stageKey(stage)} className="flex items-start gap-3">
              <div className="flex flex-col items-center text-center w-[128px] shrink-0">
                <Tooltip title={tooltipTitle}>
                  <div className="w-10 h-10 rounded-full bg-green-50 ring-2 ring-green-100 flex items-center justify-center transition-shadow hover:shadow-sm">
                    <StageIcon stage={stage} size={20} />
                  </div>
                </Tooltip>
                <span className="mt-2 text-xs font-semibold text-black-88 line-clamp-2">
                  {label}
                </span>
                {stage.timestamp && (
                  <span className="mt-0.5 text-[11px] text-gray-400">
                    {(currentLocationTimezone
                      ? dayjs.utc(stage.timestamp).tz(currentLocationTimezone)
                      : dayjs.utc(stage.timestamp).local()
                    ).format("MMM D, YYYY, h:mm A")}
                  </span>
                )}
                {stage.key === STAGE.INVITE_SENT && userName && (
                  <span className="text-[11px] text-gray-400">Sent by {userName}</span>
                )}
              </div>
              {index < enriched.length - 1 && (
                <div className="flex items-center pt-[18px] shrink-0">
                  <div className="h-[3px] w-12 bg-gradient-to-r from-green-300 to-green-200 rounded-full" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
